/**
 * Scenario authoring (completion plan P5 enabler).
 *
 * /admin/scenarios                      — the scenarios an author can open
 * /admin/scenarios/:scenarioId/progression — edit one scenario's progression
 *                                            and tags
 * /admin/scenarios/:scenarioId/preview     — "Preview assessment": the
 *                                            authoring/QA view of what an
 *                                            attempt would be held to. It
 *                                            replaces the old learner-reachable
 *                                            /assessment/preview page.
 *
 * The server enforces authority (Central platform admin + practice:write, the
 * same as scenario create/update) and validates every save; a refused call is
 * shown as such rather than hidden.
 */

import { useEffect, useMemo, useState } from 'react';
import { Link, NavLink, useParams } from 'react-router-dom';
import { useGetScenarioQuery, useListScenariosQuery } from '../../store/api/scenarioApi';
import {
  useDeleteScenarioProgressionMutation,
  useGetScenarioAssessmentPreviewQuery,
  useGetScenarioProgressionQuery,
  useListRubricsQuery,
  useSaveScenarioProgressionMutation,
  useUpdateScenarioTagsMutation,
} from '../../store/api/scenarioAuthoringApi';
import { ProgressionBuilder } from '../../components/scenarioAuthoring/ProgressionBuilder';
import { AuthoringStatus } from '../../components/scenarioAuthoring/AuthoringStatus';
import { TagsEditor } from '../../components/scenarioAuthoring/TagsEditor';
import { AssessmentPreviewPanel } from '../../components/scenarioAuthoring/AssessmentPreviewPanel';
import {
  definitionFromDraft,
  draftFromDefinition,
  emptyDraft,
  problemsFromError,
  type ProgressionDraft,
} from '../../components/scenarioAuthoring/progressionTypes';

function refusalMessage(error: unknown): string | null {
  const status = (error as { status?: number } | undefined)?.status;
  if (status === 401 || status === 403) {
    return 'Scenario authoring requires a Central platform administrator with practice:write.';
  }
  if (status === 404) return 'Scenario not found.';
  return null;
}

export function ScenarioAuthoringListPage() {
  const [page, setPage] = useState(1);
  const { data, isLoading, error } = useListScenariosQuery({ page, limit: 50 });
  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Scenario authoring</h1>
        <p className="mt-1 text-gray-500">
          Author what an attempt at a scenario is held to: phases, criteria and the rubric it is graded against.
        </p>
      </div>
      {isLoading && <p className="text-sm text-gray-500">Loading scenarios…</p>}
      {error && <p className="text-sm text-red-600">Could not load scenarios.</p>}
      {data && (
        <>
          <ul className="divide-y divide-gray-200 rounded-lg border border-gray-200 bg-white">
            {data.scenarios.map((s) => (
              <li key={s.id} className="flex items-center justify-between px-4 py-3">
                <div>
                  <p className="font-medium text-gray-900">{s.title}</p>
                  <p className="text-xs text-gray-500">{s.type} · {s.difficulty} · persona {s.persona}{s.tags?.length ? ` · ${s.tags.join(', ')}` : ''}</p>
                </div>
                <span className="flex gap-4">
                  <Link to={`/admin/scenarios/${s.id}/progression`} className="text-sm font-medium text-primary-600 hover:text-primary-700">
                    Author progression
                  </Link>
                  <Link to={`/admin/scenarios/${s.id}/preview`} className="text-sm font-medium text-primary-600 hover:text-primary-700">
                    Preview assessment
                  </Link>
                </span>
              </li>
            ))}
            {data.scenarios.length === 0 && <li className="px-4 py-3 text-sm text-gray-500">No scenarios.</li>}
          </ul>
          {data.totalPages > 1 && (
            <div className="flex items-center gap-2 text-sm">
              <button type="button" disabled={page <= 1} onClick={() => setPage(page - 1)} className="rounded border px-2 py-1 disabled:opacity-50">Previous</button>
              <span>Page {data.page} of {data.totalPages}</span>
              <button type="button" disabled={page >= data.totalPages} onClick={() => setPage(page + 1)} className="rounded border px-2 py-1 disabled:opacity-50">Next</button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

/** The two authoring tabs for one scenario. */
export function ScenarioAuthoringTabs({ scenarioId }: { scenarioId: string }) {
  const cls = ({ isActive }: { isActive: boolean }) =>
    `whitespace-nowrap border-b-2 px-1 py-2 text-sm font-medium ${isActive ? 'border-primary-500 text-primary-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`;
  return (
    <nav className="-mb-px flex gap-6 border-b border-gray-200" aria-label="Scenario authoring">
      <NavLink to={`/admin/scenarios/${scenarioId}/progression`} className={cls}>Progression</NavLink>
      <NavLink to={`/admin/scenarios/${scenarioId}/preview`} className={cls}>Preview assessment</NavLink>
    </nav>
  );
}

/** "Preview assessment": authoring/QA only — never an attempt, never a score. */
export function ScenarioAssessmentPreviewPage() {
  const { scenarioId = '' } = useParams<{ scenarioId: string }>();
  const scenario = useGetScenarioQuery(scenarioId, { skip: !scenarioId });
  const preview = useGetScenarioAssessmentPreviewQuery(scenarioId, { skip: !scenarioId });
  const refused = refusalMessage(preview.error);
  return (
    <div className="space-y-6">
      <div>
        <Link to="/admin/scenarios" className="text-sm text-primary-600 hover:text-primary-700">← Scenarios</Link>
        <h1 className="mt-1 text-2xl font-bold text-gray-900">{scenario.data?.title ?? 'Scenario'}: assessment preview</h1>
      </div>
      <ScenarioAuthoringTabs scenarioId={scenarioId} />
      {refused && <p className="text-sm text-red-600" data-testid="authoring-refused">{refused}</p>}
      {!refused && preview.error && <p className="text-sm text-red-600">Could not load the preview.</p>}
      {!preview.error && (preview.isLoading || !preview.data) && <p className="text-sm text-gray-500">Loading…</p>}
      {preview.data && <AssessmentPreviewPanel preview={preview.data} />}
    </div>
  );
}

export function ScenarioAuthoringPage() {
  const { scenarioId = '' } = useParams<{ scenarioId: string }>();
  const scenario = useGetScenarioQuery(scenarioId, { skip: !scenarioId });
  const view = useGetScenarioProgressionQuery(scenarioId, { skip: !scenarioId });
  const rubrics = useListRubricsQuery();
  const [save, saveState] = useSaveScenarioProgressionMutation();
  const [remove, removeState] = useDeleteScenarioProgressionMutation();
  const [saveTags, tagsState] = useUpdateScenarioTagsMutation();

  const [draft, setDraft] = useState<ProgressionDraft | null>(null);
  const [problems, setProblems] = useState<string[]>([]);
  const [notice, setNotice] = useState<string | null>(null);
  const [tags, setTags] = useState<string[] | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);

  // Seed the draft from the server once per load (and after a delete resets it).
  useEffect(() => {
    if (!view.data || draft) return;
    setDraft(view.data.definition ? draftFromDefinition(view.data.definition) : emptyDraft(view.data.vocabulary.defaultPhases));
    setProblems(view.data.validation.problems);
    setTags(view.data.tags);
  }, [view.data, draft]);

  const tagsDirty = useMemo(
    () => !!tags && !!view.data && JSON.stringify(tags) !== JSON.stringify(view.data.tags),
    [tags, view.data],
  );

  const refused = refusalMessage(view.error);
  if (refused) return <p className="text-sm text-red-600" data-testid="authoring-refused">{refused}</p>;
  if (view.isLoading || !view.data || !draft) return <p className="text-sm text-gray-500">Loading…</p>;

  const busy = saveState.isLoading || removeState.isLoading || tagsState.isLoading;

  const onSave = async () => {
    setNotice(null);
    try {
      const result = await save({ scenarioId, definition: definitionFromDraft(draft) }).unwrap();
      setProblems([]);
      setDraft(result.definition ? draftFromDefinition(result.definition) : draft);
      setNotice('Progression saved. Sessions already started keep the content they were bound to.');
    } catch (error) {
      const found = problemsFromError(error);
      setProblems(found ?? []);
      setNotice(found ? 'The server rejected the progression. Fix the problems shown below.' : refusalMessage(error) ?? 'Saving failed.');
    }
  };

  const onDelete = async () => {
    setConfirmDelete(false);
    setNotice(null);
    try {
      await remove(scenarioId).unwrap();
      setDraft(emptyDraft(view.data!.vocabulary.defaultPhases));
      setProblems([]);
      setNotice('Progression removed: the scenario is CONTENT_INCOMPLETE again.');
    } catch (error) {
      setNotice(refusalMessage(error) ?? 'Removing the progression failed.');
    }
  };

  const onSaveTags = async () => {
    if (!tags) return;
    try {
      await saveTags({ scenarioId, tags }).unwrap();
      setNotice('Tags saved.');
    } catch (error) {
      setNotice(refusalMessage(error) ?? 'Saving tags failed.');
    }
  };

  const v = view.data;
  return (
    <div className="space-y-6">
      <div>
        <Link to="/admin/scenarios" className="text-sm text-primary-600 hover:text-primary-700">← Scenarios</Link>
        <h1 className="mt-1 text-2xl font-bold text-gray-900">{scenario.data?.title ?? 'Scenario'}: progression</h1>
        <div className="mt-2"><AuthoringStatus view={v} /></div>
      </div>
      <ScenarioAuthoringTabs scenarioId={scenarioId} />

      {notice && <p className="rounded bg-gray-50 px-3 py-2 text-sm text-gray-700" role="status">{notice}</p>}

      <section className="rounded-lg border border-gray-200 p-4">
        <h2 className="text-sm font-semibold text-gray-900">Tags</h2>
        <p className="mt-0.5 text-xs text-gray-500">An exam counts graded attempts at scenarios carrying one of its tags.</p>
        <div className="mt-3">
          <TagsEditor tags={tags ?? []} examTags={v.examTags} onChange={setTags} disabled={busy} />
        </div>
        <button type="button" className="mt-3 rounded bg-gray-800 px-3 py-1 text-sm text-white disabled:opacity-50" disabled={busy || !tagsDirty} onClick={onSaveTags}>
          Save tags
        </button>
      </section>

      <ProgressionBuilder
        draft={draft}
        onChange={setDraft}
        concerns={v.vocabulary.concerns}
        requiredPhases={v.vocabulary.requiredPhases}
        actionKinds={v.vocabulary.actionKinds}
        conditionKinds={v.vocabulary.conditionKinds}
        rubrics={rubrics.data ?? []}
        problems={problems}
        disabled={busy}
      />

      <div className="flex flex-wrap items-center gap-3">
        <button type="button" className="rounded bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-50" disabled={busy} onClick={onSave}>
          Save progression
        </button>
        {v.definition && !confirmDelete && (
          <button type="button" className="rounded border border-red-300 px-4 py-2 text-sm text-red-700 disabled:opacity-50" disabled={busy} onClick={() => setConfirmDelete(true)}>
            Remove progression
          </button>
        )}
        {confirmDelete && (
          <span className="flex items-center gap-2 text-sm text-red-700">
            Remove it? The scenario returns to CONTENT_INCOMPLETE and cannot be graded.
            <button type="button" className="rounded bg-red-600 px-3 py-1 text-white" onClick={onDelete}>Remove</button>
            <button type="button" className="rounded border px-3 py-1" onClick={() => setConfirmDelete(false)}>Cancel</button>
          </span>
        )}
      </div>
    </div>
  );
}
